import { useMemo } from 'react';
import {
  NativeSyntheticEvent,
  TextInputFocusEventData,
  View,
} from 'react-native';
import {
  FloatingFieldHint,
  IFloatingFieldProps,
} from '@/common/components/Fields/FloatingField/FloatingField';
import { SelectField } from '@/common/components/Fields/SelectField/SelectField';
import { TextField } from '@/common/components/Fields/TextField/TextField';
import { Stack } from '@/common/components/Stack/Stack';
import { COUNTRIES, ICountry } from '@coverflex-tech/hypernova-utils';
import { BodyText } from '../../BodyText/BodyText';
import { styles } from './PhoneField.styles';

export type TPhoneFieldValue = {
  dialCode: string;
  number: string;
};

export type TPhoneCountryItem = {
  label: string;
  value: string;
  name: string;
  iso2: string;
  dialCode: string;
  flag: string;
};

export interface TPhoneFieldProps
  extends Pick<IFloatingFieldProps, 'hint' | 'disabled'> {
  ids: {
    codeField: string;
    numberField: string;
  };
  disabled?: boolean;
  value?: TPhoneFieldValue | null;
  onChange: (value: TPhoneFieldValue) => void;
  onBlur?: (evt: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  className?: string;
  allowClear?: boolean;
  language?: string;
  labels: {
    codeField: string;
    numberField: string;
  };
  errors?: {
    codeField?: string;
    numberField?: string;
  };
  suggestedCountries?: string[];
}

export function PhoneField({
  ids,
  value,
  errors,
  hint,
  onChange,
  onBlur,
  disabled,
  className,
  labels,
  suggestedCountries,
}: TPhoneFieldProps) {
  const countriesList = useMemo(() => {
    const topCountries = COUNTRIES.filter((c) =>
      suggestedCountries?.includes(c.iso2)
    );
    return topCountries
      .concat(COUNTRIES.filter((c) => !!c.dialCode))
      .map(mapCountryToItem);
  }, [suggestedCountries]);

  const country = useMemo(() => {
    return countriesList.find(
      (c) => c.value === getCountryValue(c.iso2, value?.dialCode)
    );
  }, [value?.dialCode, countriesList]);

  const showHint = !!errors?.codeField || !!errors?.numberField || !!hint;
  const withError = !disabled && (!!errors?.codeField || !!errors?.numberField);

  return (
    <View className={className}>
      <Stack horizontal gap="sm">
        <SelectField
          className={styles.codeField}
          menuClassName={styles.codeMenu}
          id={ids.codeField}
          label={labels.codeField}
          options={countriesList}
          disabled={disabled}
          value={country}
          onChange={(newCountry) => {
            onChange({
              dialCode: newCountry?.dialCode ?? '',
              number: value?.number ?? '',
            });
          }}
          onBlur={onBlur}
          renderItem={renderCountryItem}
          error={errors?.codeField}
          hintHidden
        />
        <TextField
          id={ids.numberField}
          type="numeric"
          className={styles.numberField}
          label={labels.numberField}
          inputFormatter={(value) => {
            const numerics = value.match(/\d/g);
            return numerics ? numerics.join('') : '';
          }}
          value={value?.number ?? ''}
          onChange={(_evt, formatted) =>
            onChange({
              dialCode: value?.dialCode ?? '',
              number: formatted ?? '',
            })
          }
          onBlur={onBlur}
          error={errors?.numberField}
          hintHidden
          disabled={disabled}
        />
      </Stack>
      {showHint && (
        <FloatingFieldHint invalid={withError}>
          {errors?.numberField || errors?.codeField || hint}
        </FloatingFieldHint>
      )}
    </View>
  );
}

function getCountryValue(iso2: string, dialCode?: string) {
  return `${iso2}:${dialCode}`;
}

function mapCountryToItem(country: ICountry): TPhoneCountryItem {
  return {
    ...country,
    label: country.dialCode,
    value: getCountryValue(country.iso2, country.dialCode),
  };
}

function renderCountryItem(item: TPhoneCountryItem) {
  return (
    <Stack horizontal align="center" justify="between" grow gap="sm">
      <Stack horizontal align="center" gap="sm" shrink>
        <BodyText>{item.flag}</BodyText>
        <BodyText bold>{item.name}</BodyText>
      </Stack>
      <BodyText color="neutral20">{item?.dialCode}</BodyText>
    </Stack>
  );
}
